import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../components/auth/AuthContext";
import { useIsAdmin } from "../hooks/useFeatureFlags";
import { useLicense } from "../licensing/LicenseContext";
import { getMyTasks, getMyWorkflowInstances } from "../api/workflowApi";
import { getFolders } from "../api/adminApi";
import { getWorkspaceTree, getWorkspaceFolderDocuments, createWorkspaceFolder } from "../api/workspaceApi";
import { getMyNotifications } from "../api/notificationsApi";
// Both by their own path, never through components/common's barrel: that barrel re-exports
// ResultsPanel, DocumentDetails and SearchContext, and importing it would drag the entire live
// landing into a page whose whole claim is that it shares nothing with it.
import PromptModal from "../components/common/PromptModal";
import WorkspaceUploadModal from "../components/workspace/WorkspaceUploadModal";
import "./homeCommandCenter.css";

/**
 * A SECOND landing screen, offered for comparison — not a replacement.
 *
 * The shipping home is the results pane's idle state: the summary strip, the three-layer Explorer and
 * the detail panel's lists. None of it is touched by this file. This page is mounted on its own route
 * (`/home-command-center`), imports nothing out of the live landing, and is reachable only by typing
 * the address, so it can be looked at, argued about and deleted without leaving a mark.
 *
 * <b>What it is asking</b>: whether a full-width command centre — one rail of destinations, a hero that
 * puts the four counters around a core rather than in a row of tiles, the personal workspace as large
 * shortcut cards, a row of starting points, and the lists moved into a right rail — reads better than
 * the centre-pane summary does. The treatment is an instrument panel: hairlines, chamfered corners,
 * caps microtype, black as the primary, and colour spent only where it carries meaning — each
 * counter's own hue and the workspace bars.
 *
 * <b>Everything on it is real.</b> The counters, the workspace cards, the lists and the folders come from
 * the same endpoints the live landing uses, so what you are judging is the layout under real data and
 * not a picture of it. The one thing that is not wired is the search field: the product's search lives
 * in a provider mounted on the main screen, and reaching into it from here would mean changing the
 * shipping page. Submitting hands over to that screen instead, without carrying the text.
 *
 * Cost is the same shape as the live landing's: the per-user workflow tables, the folder list, and the
 * caller's own workspace. The only addition is one document count per workspace shortcut — three
 * counts, each scoped to one person's folder. Nothing here touches a document table unscoped.
 */

/* ─── Icons ───────────────────────────────────────────────────────────────────────────────────── */
const I = {
  home: (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M3 10.5 12 3l9 7.5" /><path d="M5 9.5V20a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9.5" /></svg>
  ),
  briefcase: (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="1.5" /><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
  ),
  folder: (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" /></svg>
  ),
  flow: (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="2.6" /><circle cx="6" cy="12" r="2.6" /><circle cx="18" cy="19" r="2.6" /><path d="m8.6 13.4 6.9 4.1M15.5 6.5l-6.9 4.1" /></svg>
  ),
  doc: (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
  ),
  search: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7.5" /><path d="m21 21-4.35-4.35" /></svg>
  ),
  bookmark: (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" /></svg>
  ),
  plus: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
  ),
  inbox: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 16 12 14 15 10 15 8 12 2 12" /><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" /></svg>
  ),
  inboxSm: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 16 12 14 15 10 15 8 12 2 12" /><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" /></svg>
  ),
  clock: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><polyline points="12 6.5 12 12 15.5 14" /></svg>
  ),
  flowLg: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="8" height="6" rx="1" /><rect x="13" y="15" width="8" height="6" rx="1" /><path d="M7 9v6a2 2 0 0 0 2 2h4" /></svg>
  ),
  folderLg: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" /></svg>
  ),
  bell: (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></svg>
  ),
  chevronRight: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
  ),
  chevronDown: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
  ),
  arrowRight: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="12" x2="20" y2="12" /><polyline points="13 5 20 12 13 19" /></svg>
  ),
  sliders: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round"><line x1="3" y1="7" x2="21" y2="7" /><line x1="3" y1="17" x2="21" y2="17" /><circle cx="9" cy="7" r="2.4" fill="currentColor" stroke="none" /><circle cx="16" cy="17" r="2.4" fill="currentColor" stroke="none" /></svg>
  ),
  grid: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7.5" height="7.5" /><rect x="13.5" y="3" width="7.5" height="7.5" /><rect x="13.5" y="13.5" width="7.5" height="7.5" /><rect x="3" y="13.5" width="7.5" height="7.5" /></svg>
  ),
  rows: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round"><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></svg>
  ),
  listDense: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><line x1="9" y1="5" x2="20" y2="5" /><line x1="9" y1="12" x2="20" y2="12" /><line x1="9" y1="19" x2="20" y2="19" /><line x1="4" y1="5" x2="5" y2="5" /><line x1="4" y1="12" x2="5" y2="12" /><line x1="4" y1="19" x2="5" y2="19" /></svg>
  ),
  upload: (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
  ),
  database: (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="5" rx="8.5" ry="2.8" /><path d="M3.5 5v14c0 1.55 3.8 2.8 8.5 2.8s8.5-1.25 8.5-2.8V5" /><path d="M3.5 12c0 1.55 3.8 2.8 8.5 2.8s8.5-1.25 8.5-2.8" /></svg>
  ),
  shield: (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><polyline points="9 12 11 14 15 10" /></svg>
  ),
  report: (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="9" y1="17" x2="9" y2="13" /><line x1="12" y1="17" x2="12" y2="11" /><line x1="15" y1="17" x2="15" y2="15" /></svg>
  ),
  spark: (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3 2.1 5.4L19.5 10l-5.4 2.1L12 17.5l-2.1-5.4L4.5 10l5.4-1.6z" /><path d="M18.5 16.5 19 18l1.5.5L19 19l-.5 1.5-.5-1.5L16.5 18l1.5-.5z" /></svg>
  ),
  workflowStep: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="5" rx="1" /><rect x="14" y="16" width="7" height="5" rx="1" /><path d="M6.5 8v6a2 2 0 0 0 2 2h5.5" /></svg>
  ),
  moreDots: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="19" cy="12" r="1.6" /></svg>
  ),
};

/** The section marker the drawing uses. Decoration only, so it never reaches a screen reader. */
function Slash({ n = 1, ink = false }) {
  return <span className={`cc-slash${ink ? " cc-slash--ink" : ""}`} aria-hidden="true">{"/".repeat(n)}</span>;
}

/** The product mark, drawn rather than loaded so this page pulls in no asset of its own. */
function BrandMark() {
  return (
    <svg width="30" height="34" viewBox="0 0 30 34" aria-hidden="true">
      <path d="M4 3h11c6.6 0 11 4.9 11 12.6S21.6 31 15 31H4z" fill="none" stroke="#fff" strokeWidth="2.4" />
      <path d="M11 10h4.5c3 0 4.8 2.2 4.8 6.2S18.5 24 15.5 10.5" fill="none" stroke="#fff" strokeWidth="1.1" opacity=".5" />
    </svg>
  );
}

/** The core of the instrument: a hexagon with the archive inside it. */
function OrbitCore() {
  return (
    <svg width="104" height="118" viewBox="0 0 104 118" aria-hidden="true">
      <path d="M52 4 96 30v58L52 114 8 88V30z" fill="#fff" stroke="#dfe3ea" strokeWidth="1.2" />
      <path d="M52 13 88 34v50L52 105 16 84V34z" fill="none" stroke="#eef0f4" strokeWidth="1" />
      <g transform="translate(34 46)" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M35 24a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8l2 3h20a2 2 0 0 1 2 2z" />
      </g>
    </svg>
  );
}

/** The rings and the plotted points behind the counters. Decoration: none of it carries a value. */
function OrbitRings() {
  const dots = [
    [352, 40, "#0b0d13"], [268, 78, "#2563eb"], [452, 66, "#2563eb"],
    [212, 150, "#7c3aed"], [500, 152, "#10b981"], [300, 214, "#2563eb"],
    [420, 220, "#0b0d13"], [360, 244, "#c9cdd6"], [174, 108, "#c9cdd6"], [548, 196, "#c9cdd6"],
  ];
  return (
    <svg className="cc-orbit-rings" viewBox="0 0 720 268" preserveAspectRatio="none" aria-hidden="true">
      <g fill="none" stroke="#e9ebf0">
        <ellipse cx="360" cy="134" rx="196" ry="122" strokeDasharray="2 7" />
        <ellipse cx="360" cy="134" rx="158" ry="99" />
        <ellipse cx="360" cy="134" rx="118" ry="74" strokeDasharray="2 6" />
        <ellipse cx="360" cy="134" rx="78" ry="49" opacity=".8" />
      </g>
      <g stroke="#f1f2f6"><path d="M164 134h392M360 12v244" /></g>
      {dots.map(([x, y, fill], i) => (
        <circle key={i} cx={x} cy={y} r={i % 3 === 0 ? 3.4 : 2.6} fill={fill} />
      ))}
    </svg>
  );
}

/** The glyph on a workspace shortcut. Index-driven: a folder anyone can rename cannot be read for it. */
function WsGlyph({ kind }) {
  const tone = { blue: "#2563eb", violet: "#7c3aed", green: "#10b981" }[kind] || "#2563eb";
  const common = { fill: "none", stroke: tone, strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" };
  if (kind === "violet") {
    return (
      <svg width="60" height="60" viewBox="0 0 48 48" aria-hidden="true">
        <g {...common}><rect x="5" y="11" width="38" height="26" rx="3" /><path d="m6 13 18 14 18-14" /></g>
      </svg>
    );
  }
  if (kind === "green") {
    return (
      <svg width="60" height="60" viewBox="0 0 48 48" aria-hidden="true">
        <g {...common}><path d="M8 34.5V40h5.5L38 15.5 32.5 10z" /><path d="M29 13.5 34.5 19" /></g>
      </svg>
    );
  }
  return (
    <svg width="60" height="60" viewBox="0 0 48 48" aria-hidden="true">
      <g {...common}><path d="M28 5H12a3 3 0 0 0-3 3v32a3 3 0 0 0 3 3h24a3 3 0 0 0 3-3V16z" /><path d="M28 5v11h11" /><path d="M16 25h16M16 32h11" /></g>
    </svg>
  );
}

/** The hexagon frame a starting point's icon sits in. */
function HexFrame() {
  return (
    <svg className="cc-hex" viewBox="0 0 40 44" aria-hidden="true">
      <path d="M20 2 37 12v20L20 42 3 32V12z" fill="none" stroke="currentColor" strokeWidth="1.1" opacity=".45" />
    </svg>
  );
}

/** The empty state's own mark: the same ring diagram, quieted right down. */
function QuietMark() {
  return (
    <svg width="96" height="96" viewBox="0 0 96 96" aria-hidden="true">
      <g fill="none" stroke="#e9ebf0">
        <circle cx="48" cy="48" r="45" strokeDasharray="2 7" />
        <circle cx="48" cy="48" r="34" />
        <circle cx="48" cy="48" r="23" strokeDasharray="2 6" />
      </g>
      <circle cx="48" cy="3" r="2.4" fill="#2563eb" /><circle cx="84" cy="66" r="2.2" fill="#c9cdd6" />
      <g transform="translate(36 39)" fill="none" stroke="#9aa0ac" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <rect x="1" y="1" width="22" height="17" rx="2" /><path d="m2 3 10 7 10-7" />
      </g>
    </svg>
  );
}

/** A flat line with a pulse in it. It is a mark, not a measurement, and is hidden from the tree. */
function Sparkline({ bad }) {
  return (
    <svg className="cc-status-spark" viewBox="0 0 200 30" preserveAspectRatio="none" aria-hidden="true">
      <path
        d="M0 22h34l6-11 5 15 5-8 6 4 5-9 6 12 5-6h122"
        fill="none" stroke={bad ? "#dc2626" : "#2563eb"} strokeWidth="1.6" strokeLinejoin="round" vectorEffect="non-scaling-stroke"
      />
      <circle className="cc-status-spark-dot" cx="4" cy="22" r="3" fill={bad ? "#dc2626" : "#2563eb"} />
    </svg>
  );
}

function fmtWhen(v, t) {
  if (!v) return "";
  const dt = new Date(v);
  if (isNaN(dt)) return String(v);
  const mins = Math.round((Date.now() - dt.getTime()) / 60000);
  if (mins < 1) return t("home.justNow", { defaultValue: "just now" });
  if (mins < 60) return t("home.minutesAgo", { defaultValue: "{{count}}m ago", count: mins });
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return t("home.hoursAgo", { defaultValue: "{{count}}h ago", count: hrs });
  const days = Math.round(hrs / 24);
  if (days < 30) return t("home.daysAgo", { defaultValue: "{{count}}d ago", count: days });
  return dt.toLocaleDateString();
}

/** What the detail pane's "recently viewed" reads — the same list, from the same place. */
function loadRecentDocs() {
  try {
    const raw = JSON.parse(localStorage.getItem("recent_docs_v1") || "[]");
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
}

function Panel({ marks, icon, title, count, action, children, foot }) {
  return (
    <section className="cc-card cc-panel cc-cut cc-cut--br">
      <header className="cc-panel-head">
        <h3 className="cc-panel-title"><Slash n={marks || 1} />{title}</h3>
        {count != null && <span className="cc-panel-count">{count}</span>}
        {action}
      </header>
      {children}
      {foot && <div className="cc-panel-foot">{foot}</div>}
    </section>
  );
}

export default function HomeCommandCenter() {
  const { t, i18n } = useTranslation();
  const { token, user } = useAuth();
  const isAdmin = useIsAdmin();
  const { hasFeature, featuresResolved } = useLicense();
  const navigate = useNavigate();
  const govEnabled = hasFeature ? hasFeature("content-governance") : true;
  // Optimistic for rendering, resolved for fetching — see LicenseContext.featuresResolved.
  const govFetchReady = govEnabled && featuresResolved;

  const [tasks, setTasks] = useState([]);
  const [instances, setInstances] = useState({ items: [], total: 0, runningTotal: 0 });
  // Same three-way answer the live summary gives, for the same reason: "not licensed" and "the request
  // did not land" are different things to tell someone deciding whether they have work waiting.
  const [wfState, setWfState] = useState(null);
  const [orgFolders, setOrgFolders] = useState([]);
  const [shortcuts, setShortcuts] = useState([]);
  const [notifCount, setNotifCount] = useState(null);
  const [degraded, setDegraded] = useState(false);
  const [recent] = useState(loadRecentDocs);
  const [layout, setLayout] = useState("grid");
  const searchRef = useRef(null);

  // What the starting points actually do. Each of these is a real mutation of this person's
  // workspace, so each has to say what happened — success included, since a document that has been
  // archived and filed with no acknowledgement is indistinguishable from one that was dropped.
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [uploadInto, setUploadInto] = useState(null);   // the folder the upload dialog is aimed at
  const [notice, setNotice] = useState(null);           // { kind: "ok" | "bad", text }

  /* The clock in the hero. A second's tick is the cheapest possible proof that the screen is live. */
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  /* ⌘K / Ctrl-K puts the caret in the search field, which is what the chip in it promises. */
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && String(e.key).toLowerCase() === "k") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /* ── the work lists ─────────────────────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!token || !govFetchReady) { setWfState(govEnabled ? null : "absent"); return undefined; }
    let alive = true;
    (async () => {
      try {
        const [myTasks, mine] = await Promise.all([
          getMyTasks(token, false),
          getMyWorkflowInstances(token, { includeCompleted: true, take: 25 }),
        ]);
        if (!alive) return;
        setTasks(Array.isArray(myTasks) ? myTasks : []);
        setInstances({
          items: Array.isArray(mine?.items) ? mine.items : [],
          total: Number(mine?.total) || 0,
          runningTotal: Number(mine?.runningTotal) || 0,
        });
        setWfState(null);
      } catch (err) {
        if (!alive) return;
        const status = Number(err?.status) || 0;
        const absent = status === 403 || status === 404 || status === 501;
        setWfState(absent ? "absent" : "failed");
        if (!absent) setDegraded(true);
      }
    })();
    return () => { alive = false; };
  }, [token, govFetchReady, govEnabled]);

  /* ── the company's folder plan ──────────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!token || !govFetchReady) return undefined;
    let alive = true;
    getFolders(token)
      .then((list) => alive && setOrgFolders((Array.isArray(list) ? list : []).filter((f) => f && f.id != null && f.isActive)))
      .catch(() => { if (alive) { setOrgFolders([]); setDegraded(true); } });
    return () => { alive = false; };
  }, [token, govFetchReady]);

  /* ── the caller's own workspace, and how much is in each shortcut ─────────────────────────────
     A run counter rather than a per-effect flag, because this now reloads on demand too: an upload
     that finishes after a language switch must not overwrite the newer answer with its own. */
  const wsRun = useRef(0);
  const loadWorkspace = useCallback(async () => {
    if (!token) return;
    const run = ++wsRun.current;
    let roots = [];
    try {
      const tree = await getWorkspaceTree(token, i18n?.language);
      roots = (tree?.folders || [])
        .filter((f) => f && f.isActive !== false && (f.parentId ?? null) === null)
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || String(a.name || "").localeCompare(String(b.name || "")))
        .slice(0, 3);
    } catch {
      if (run === wsRun.current) { setShortcuts([]); setDegraded(true); }
      return;
    }
    if (run !== wsRun.current) return;
    // The count is shown as "at least this many" past the cap rather than as a number we did not
    // finish counting — a shortcut that quietly under-reports is worse than one that says so.
    setShortcuts(roots.map((f) => ({ ...f, count: null, truncated: false })));
    const counted = await Promise.all(roots.map(async (f) => {
      try {
        const res = await getWorkspaceFolderDocuments(token, f.id, { includeSubtree: true, max: 200 });
        return { ...f, count: Number(res?.count) || 0, truncated: !!res?.truncated };
      } catch {
        return { ...f, count: null, truncated: false };
      }
    }));
    if (run === wsRun.current) setShortcuts(counted);
  }, [token, i18n?.language]);

  useEffect(() => { loadWorkspace(); }, [loadWorkspace]);

  /* ── the bell ───────────────────────────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!token) return undefined;
    let alive = true;
    getMyNotifications(token)
      .then((items) => alive && setNotifCount(Array.isArray(items) ? items.length : 0))
      .catch(() => alive && setNotifCount(null));
    return () => { alive = false; };
  }, [token]);

  const overdue = useMemo(() => tasks.filter((x) => x.isOverdue).length, [tasks]);
  const rootFolders = useMemo(
    () => orgFolders
      .filter((f) => (f.parentId ?? null) === null)
      .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""))),
    [orgFolders]
  );
  const childCount = useCallback(
    (id) => orgFolders.filter((f) => (f.parentId ?? null) === id).length,
    [orgFolders]
  );

  const showWorkflow = govEnabled && wfState !== "absent";
  const wfCount = (n) => (wfState === "failed" ? "—" : n);
  const displayName = user?.fullName || user?.name || user?.userName || user?.email?.split("@")[0] || "";
  const initial = (displayName || "?").trim().charAt(0);

  const maxShortcut = useMemo(
    () => Math.max(1, ...shortcuts.map((s) => Number(s.count) || 0)),
    [shortcuts]
  );

  /* The search field belongs to the main screen. Rather than pretend, this hands over to it. */
  const handoff = useCallback((e) => {
    e.preventDefault();
    navigate("/");
  }, [navigate]);

  /* A result that is only on screen for a moment: long enough to read, gone before it is clutter.
     Failures stay until they are dismissed — a message you missed is a change you think happened. */
  useEffect(() => {
    if (!notice || notice.kind !== "ok") return undefined;
    const id = setTimeout(() => setNotice(null), 6000);
    return () => clearTimeout(id);
  }, [notice]);

  /* NEW FOLDER / ADD SHORTCUT — the same act: a folder at the top of this person's workspace. */
  const createFolder = useCallback(async (name) => {
    setCreating(true);
    try {
      await createWorkspaceFolder(token, { parentId: null, name });
      setNewFolderOpen(false);
      setNotice({ kind: "ok", text: t("homeCc.folderCreated", { defaultValue: "Folder created." }) });
      loadWorkspace();
    } catch (e) {
      // The server names the actual obstacle — a duplicate name, a base still waiting on a DBA —
      // and that is far more use than "could not be created".
      setNewFolderOpen(false);
      setNotice({ kind: "bad", text: e?.message || t("workspace.uploadFailed", { defaultValue: "The document could not be uploaded." }) });
    } finally {
      setCreating(false);
    }
  }, [token, t, loadWorkspace]);

  /* UPLOAD — a real document into a real folder. The destination is asked for rather than assumed:
     picking one for someone silently is how a document ends up somewhere nobody looks. */
  const startUpload = useCallback(() => {
    if (shortcuts.length === 0) {
      setNotice({ kind: "bad", text: t("homeCc.needFolderFirst", { defaultValue: "Create a folder before adding a document." }) });
      setNewFolderOpen(true);
      return;
    }
    if (shortcuts.length === 1) { setUploadInto(shortcuts[0]); return; }
    setPickerOpen(true);
  }, [shortcuts, t]);

  const onUploaded = useCallback(() => {
    setNotice({
      kind: "ok",
      text: t("homeCc.filedInto", { defaultValue: "Filed into {{folder}}.", folder: uploadInto?.name || "" }),
    });
    loadWorkspace();
  }, [uploadInto, t, loadWorkspace]);

  /* Escape closes the destination picker, the way it closes every other dialog in the product. */
  useEffect(() => {
    if (!pickerOpen) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setPickerOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [pickerOpen]);

  // A rail that lists a destination this tenant does not have is the same lie as a counter that
  // under-reports: workflow entries come and go with the licence, exactly as the panels do.
  const railItems = [
    { key: "home", icon: I.home, label: t("nav.home", { defaultValue: "Home" }), to: "/home-command-center", active: true, dot: true },
    { key: "workspace", icon: I.briefcase, label: t("workspace.home", { defaultValue: "My Workspace" }), to: "/" },
    govEnabled && { key: "folders", icon: I.folder, label: t("home.kpi.folders", { defaultValue: "Folders" }), to: "/" },
    showWorkflow && { key: "workflows", icon: I.flow, label: t("sidebar.workflows", { defaultValue: "Workflows" }), to: "/my-tasks" },
    { key: "documents", icon: I.doc, label: t("homeCc.documents", { defaultValue: "Documents" }), to: "/" },
  ].filter(Boolean);
  const discoverItems = [
    { key: "searches", icon: I.search, label: t("homeCc.searches", { defaultValue: "Searches" }), to: "/search-builder" },
    { key: "saved", icon: I.bookmark, label: t("sidebar.savedViews", { defaultValue: "Saved Views" }), to: "/" },
  ];

  // Only what this person can actually reach. A row full of starting points that bounce off the admin
  // guard would be the same lie as a counter that under-reports.
  const quickActions = [
    { key: "upload", tone: "blue", icon: I.upload, onClick: startUpload,
      title: t("workspace.upload", { defaultValue: "Add document" }),
      label: t("batch.upload", { defaultValue: "Upload" }), sub: t("homeCc.qa.uploadSub", { defaultValue: "Document" }) },
    { key: "workflow", tone: "orange", icon: I.flow, to: "/flow-designer", needsWorkflow: true,
      label: t("homeCc.qa.create", { defaultValue: "Create" }), sub: t("homeCc.qa.createSub", { defaultValue: "Workflow" }) },
    { key: "database", tone: "indigo", icon: I.database, to: "/admin/db-config", adminOnly: true,
      label: t("homeCc.qa.add", { defaultValue: "Add" }), sub: t("admin.database", { defaultValue: "Database" }) },
    { key: "approval", tone: "green", icon: I.shield, to: "/my-tasks", needsWorkflow: true,
      label: t("homeCc.qa.request", { defaultValue: "Request" }), sub: t("workflow.nodeType.approval", { defaultValue: "Approval" }) },
    { key: "report", tone: "violet", icon: I.report, to: "/admin/monitoring", adminOnly: true,
      label: t("homeCc.qa.generate", { defaultValue: "Generate" }), sub: t("homeCc.qa.generateSub", { defaultValue: "Report" }) },
    // The one starting point that cannot finish here: the product's search lives in a provider on the
    // main screen. So it does the honest half — puts the caret in the field that asks the question.
    { key: "ai", tone: "cyan", icon: I.spark, onClick: () => searchRef.current?.focus(),
      title: t("homeCc.askAiHint", { defaultValue: "Puts the caret in the search field above." }),
      label: t("homeCc.qa.ask", { defaultValue: "Ask AI" }), sub: t("homeCc.qa.askSub", { defaultValue: "Smart search" }) },
  ].filter((a) => (!a.adminOnly || isAdmin) && (!a.needsWorkflow || showWorkflow));

  const tones = ["blue", "violet", "green"];

  return (
    <div className="cc-root">
      {/* ── Masthead ─────────────────────────────────────────────────────────────────────────── */}
      <header className="cc-topbar">
        <div className="cc-brand">
          <span className="cc-brand-mark"><BrandMark /></span>
          <span className="cc-wordmark"><Slash n={3} />{t("homeCc.commandCenter", { defaultValue: "Command center" })}</span>
        </div>

        <form className="cc-searchwrap" onSubmit={handoff}>
          {I.search}
          <input
            ref={searchRef}
            className="cc-search"
            type="search"
            placeholder={t("homeCc.searchPlaceholder", { defaultValue: "Ask in natural language, e.g. invoices from last June for ACME" })}
            title={t("homeCc.searchHandoff", { defaultValue: "The live search bar is on the main screen. In this draft, submitting takes you there — the text is not carried over." })}
          />
          <span className="cc-kbd" aria-hidden="true">⌘ K</span>
        </form>

        <div className="cc-topbar-right">
          <Link className="cc-bell" to="/notifications" title={t("homeCc.notifications", { defaultValue: "Notifications" })}>
            {I.bell}
            {notifCount ? <span className="cc-bell-badge">{notifCount > 99 ? "99+" : notifCount}</span> : null}
          </Link>
          <Link className="cc-who" to="/">
            <span className="cc-avatar">{initial}</span>
            <span className="cc-who-name">{displayName}</span>
            {I.chevronDown}
          </Link>
        </div>
      </header>

      <div className="cc-shell">
        {/* ── Rail ───────────────────────────────────────────────────────────────────────────── */}
        <aside className="cc-rail">
          <div className="cc-rail-scroll">
            <div className="cc-rail-group">
              {railItems.map((it) => (
                <Link
                  key={it.key}
                  to={it.to}
                  className={`cc-rail-item${it.active ? " cc-rail-item--active cc-cut cc-cut--br" : ""}`}
                >
                  {it.icon}<span>{it.label}</span>{it.dot && <span className="cc-rail-dot" />}
                </Link>
              ))}
            </div>

            <div className="cc-rail-group">
              <div className="cc-rail-label">{t("homeCc.discover", { defaultValue: "Discover" })}</div>
              {discoverItems.map((it) => (
                <Link key={it.key} to={it.to} className="cc-rail-item">{it.icon}<span>{it.label}</span></Link>
              ))}
            </div>

            {showWorkflow && (
              <div className="cc-rail-group">
                <div className="cc-rail-label">{t("sidebar.workflows", { defaultValue: "Workflows" })}</div>
                <Link to="/my-tasks" className="cc-rail-item">
                  {I.workflowStep}<span>{t("home.tasks.title", { defaultValue: "Waiting on you" })}</span>
                </Link>
                <Link to="/my-tasks" className="cc-rail-item">
                  {I.workflowStep}<span>{t("home.workflows.title", { defaultValue: "Workflows you started" })}</span>
                </Link>
                <Link to="/flow-designer" className="cc-rail-item cc-rail-item--cta cc-cut cc-cut--br">
                  {I.plus}<span>{t("homeCc.newWorkflow", { defaultValue: "New workflow" })}</span>
                </Link>
              </div>
            )}
          </div>

          <Link className="cc-rail-status cc-cut cc-cut--bl" to={isAdmin ? "/admin/monitoring" : "/"}>
            <span className="cc-status-head">
              <span className="cc-status-title">{t("homeCc.systemStatus", { defaultValue: "System status" })}</span>
              {I.chevronRight}
            </span>
            <span className={`cc-status-line${degraded ? " cc-status-line--bad" : ""}`}>
              {degraded
                ? t("homeCc.someChecksFailed", { defaultValue: "Some panels could not be loaded" })
                : t("homeCc.allOperational", { defaultValue: "Everything this page needs answered" })}
            </span>
            <Sparkline bad={degraded} />
          </Link>
        </aside>

        {/* ── Canvas ─────────────────────────────────────────────────────────────────────────── */}
        <div className="cc-canvas">
          <div className="cc-main">
            {/* ── Hero ─────────────────────────────────────────────────────────────────────── */}
            <section className="cc-card cc-hero cc-cut cc-cut--tr" style={{ "--cut": "22px" }}>
              <div>
                <div className="cc-eyebrow"><Slash n={2} ink />{t("homeCc.missionControl", { defaultValue: "Mission control" })}</div>
                <h1 className="cc-hero-title">
                  {displayName
                    ? t("home.welcomeNamed", { defaultValue: "Welcome back, {{name}}", name: displayName })
                    : t("home.welcome", { defaultValue: "Welcome back" })}
                  <span className="cc-hero-tick" aria-hidden="true" />
                </h1>
                <p className="cc-hero-sub">{t("homeCc.tagline", { defaultValue: "Your command center for mission-critical content." })}</p>
                <div className="cc-hero-clock">
                  {now.toISOString().slice(11, 19)} UTC &nbsp;·&nbsp; {now.toLocaleDateString(i18n?.language || undefined, { year: "numeric", month: "long", day: "numeric" })}
                </div>
                <div className="cc-hero-actions">
                  {/* The draft proposes letting someone choose their own panels. There is nothing behind
                      it yet, and a button that silently does nothing is worse than one that says so. */}
                  <button
                    type="button"
                    className="cc-btn cc-btn--proposed cc-cut cc-cut--br"
                    disabled
                    title={t("homeCc.customizeSoon", { defaultValue: "Proposed by this draft — not built yet." })}
                  >
                    {I.sliders}{t("homeCc.customize", { defaultValue: "Customize dashboard" })}
                  </button>
                </div>
              </div>

              <div className="cc-orbit">
                <OrbitRings />
                {showWorkflow ? (
                  <Link className="cc-node cc-node--nw cc-cut cc-cut--tr" to="/my-tasks">
                    <span className="cc-node-top">
                      <span className="cc-node-icon cc-node-icon--blue">{I.inbox}</span>
                      <span className="cc-node-value">{wfCount(tasks.length)}</span>
                    </span>
                    <span className="cc-node-label">{t("home.kpi.waiting", { defaultValue: "Waiting on you" })}</span>
                    <span className="cc-node-bar cc-node-bar--blue" />
                  </Link>
                ) : <span className="cc-node cc-node--nw cc-node--empty" />}

                {showWorkflow ? (
                  <Link className="cc-node cc-node--ne cc-cut cc-cut--tr" to="/my-tasks">
                    <span className="cc-node-top">
                      <span className="cc-node-icon cc-node-icon--orange">{I.clock}</span>
                      <span className="cc-node-value">{wfCount(overdue)}</span>
                    </span>
                    <span className="cc-node-label">{t("home.kpi.overdue", { defaultValue: "Overdue" })}</span>
                    <span className="cc-node-bar cc-node-bar--orange" />
                  </Link>
                ) : <span className="cc-node cc-node--ne cc-node--empty" />}

                <div className="cc-orbit-core"><OrbitCore /></div>

                {showWorkflow ? (
                  <div className="cc-node cc-node--sw cc-cut cc-cut--bl">
                    <span className="cc-node-top">
                      <span className="cc-node-icon cc-node-icon--violet">{I.flowLg}</span>
                      <span className="cc-node-value">{wfCount(instances.runningTotal)}</span>
                    </span>
                    <span className="cc-node-label">{t("home.kpi.myWorkflowsRunning", { defaultValue: "Workflows running" })}</span>
                    <span className="cc-node-bar cc-node-bar--violet" />
                  </div>
                ) : <span className="cc-node cc-node--sw cc-node--empty" />}

                <div className="cc-node cc-node--se cc-cut cc-cut--bl">
                  <span className="cc-node-top">
                    <span className="cc-node-icon cc-node-icon--green">{I.folderLg}</span>
                    <span className="cc-node-value">{orgFolders.length}</span>
                  </span>
                  <span className="cc-node-label">{t("home.kpi.folders", { defaultValue: "Folders" })}</span>
                  <span className="cc-node-bar cc-node-bar--green" />
                </div>
              </div>
            </section>

            {/* ── The personal workspace, as shortcuts ─────────────────────────────────────── */}
            <section className="cc-card cc-cut cc-cut--tr">
              <div className="cc-card-head">
                <h2 className="cc-card-title"><Slash />{t("workspace.home", { defaultValue: "My Workspace" })}</h2>
                <div className="cc-card-head-tools">
                  <span className="cc-tools-label">{t("homeCc.view", { defaultValue: "View" })}</span>
                  <div className="cc-seg">
                    <button type="button" aria-pressed={layout === "grid"} onClick={() => setLayout("grid")}>
                      {I.grid}{t("homeCc.grid", { defaultValue: "Grid" })}
                    </button>
                    <button type="button" aria-pressed={layout === "list"} onClick={() => setLayout("list")}>
                      {I.rows}{t("homeCc.list", { defaultValue: "List" })}
                    </button>
                  </div>
                  <Link
                    className="cc-btn cc-btn--icon"
                    to="/"
                    aria-label={t("homeCc.openWorkspace", { defaultValue: "Open the full workspace" })}
                    title={t("homeCc.openWorkspace", { defaultValue: "Open the full workspace" })}
                  >
                    {I.listDense}
                  </Link>
                  <button type="button" className="cc-btn cc-btn--primary cc-cut cc-cut--br" onClick={() => setNewFolderOpen(true)}>
                    {I.plus}{t("workspace.newFolder", { defaultValue: "New folder" })}
                  </button>
                </div>
              </div>

              <div className={`cc-shortcuts${layout === "list" ? " cc-shortcuts--list" : ""}`}>
                {shortcuts.map((f, i) => (
                  <Link
                    key={f.id}
                    className="cc-shortcut cc-cut cc-cut--br"
                    to="/"
                    title={t("homeCc.openWorkspace", { defaultValue: "Open the full workspace" })}
                  >
                    <span className="cc-shortcut-art">
                      <span className="cc-shortcut-index">{String(i + 1).padStart(2, "0")}</span>
                      <span className="cc-shortcut-menu" aria-hidden="true">{I.moreDots}</span>
                      <WsGlyph kind={tones[i % 3]} />
                    </span>
                    <span className="cc-shortcut-body">
                      <span className="cc-shortcut-name">{f.name}</span>
                      <span className="cc-shortcut-count">
                        {f.count == null
                          ? t("homeCc.countUnknown", { defaultValue: "count unavailable" })
                          : t("homeCc.items", { defaultValue: "{{count}} items", count: f.count }) + (f.truncated ? "+" : "")}
                      </span>
                    </span>
                    <span className="cc-shortcut-foot">
                      <span className={`cc-shortcut-bar cc-shortcut-bar--${tones[i % 3]}`}>
                        <i style={{ width: `${Math.round(((Number(f.count) || 0) / maxShortcut) * 100)}%` }} />
                      </span>
                      <span className="cc-shortcut-go">{I.arrowRight}</span>
                    </span>
                  </Link>
                ))}
                <button type="button" className="cc-add cc-cut cc-cut--br" onClick={() => setNewFolderOpen(true)}>
                  <span className="cc-shortcut-index">{String(shortcuts.length + 1).padStart(2, "0")}</span>
                  {I.plus}{t("homeCc.addShortcut", { defaultValue: "Add shortcut" })}
                </button>
              </div>
            </section>

            {/* ── Starting points ──────────────────────────────────────────────────────────── */}
            <section className="cc-card cc-cut cc-cut--br">
              <div className="cc-card-head">
                <h2 className="cc-card-title"><Slash />{t("homeCc.quickActions", { defaultValue: "Quick actions" })}</h2>
              </div>
              <div className="cc-quick">
                {quickActions.map((a) => {
                  const inside = (
                    <>
                      <span className={`cc-quick-icon cc-quick-icon--${a.tone}`}><HexFrame />{a.icon}</span>
                      <span className="cc-quick-text">
                        <span className="cc-quick-label">{a.label}</span>
                        <span className="cc-quick-sub">{a.sub}</span>
                      </span>
                    </>
                  );
                  const cls = "cc-quick-item cc-cut cc-cut--br";
                  const cut = { "--cut": "10px" };
                  // One that DOES something is a button; one that takes you somewhere is a link. The
                  // difference is not cosmetic — it is what the keyboard and a screen reader are told.
                  return a.onClick ? (
                    <button key={a.key} type="button" className={cls} style={cut} onClick={a.onClick} title={a.title}>
                      {inside}
                    </button>
                  ) : (
                    <Link key={a.key} className={cls} style={cut} to={a.to} title={a.title}>{inside}</Link>
                  );
                })}
              </div>
            </section>
          </div>

          {/* ── Right rail ─────────────────────────────────────────────────────────────────── */}
          <div className="cc-side">
            {showWorkflow && (
              <Panel
                title={t("home.tasks.title", { defaultValue: "Waiting on you" })}
                action={<Link className="cc-panel-link" to="/my-tasks">{t("home.viewAll", { defaultValue: "View all" })}</Link>}
              >
                {tasks.length === 0 ? (
                  <div className="cc-empty">
                    <QuietMark />
                    <div className="cc-empty-text">
                      {wfState === "failed"
                        ? t("home.loadFailed", { defaultValue: "Could not be loaded. Try Refresh." })
                        : t("home.tasks.empty", { defaultValue: "Nothing is waiting on your decision." })}
                    </div>
                  </div>
                ) : (
                  <ul className="cc-rows">
                    {tasks.slice(0, 5).map((task) => (
                      <li key={task.id}>
                        <Link className="cc-row" to="/my-tasks">
                          <span className="cc-row-main">
                            <span className="cc-row-title">{task.subjectTitle || task.archiveNo || task.name || `#${task.id}`}</span>
                            <span className="cc-row-sub">{task.definitionName ? `${task.definitionName} · ` : ""}{task.name}</span>
                          </span>
                          <span className="cc-row-side">
                            {task.isOverdue && <span className="cc-chip cc-chip--red">{t("home.chip.overdue", { defaultValue: "Overdue" })}</span>}
                            {task.isPoolTask && <span className="cc-chip cc-chip--grey">{t("home.chip.unclaimed", { defaultValue: "Unclaimed" })}</span>}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </Panel>
            )}

            {showWorkflow && (
              <Panel
                title={t("home.workflows.title", { defaultValue: "Workflows you started" })}
                count={wfState === "failed" ? null : (instances.total || null)}
                foot={instances.items.length > 0
                  ? <Link to="/my-tasks">{t("homeCc.viewAllWorkflows", { defaultValue: "View all workflows" })}{I.arrowRight}</Link>
                  : null}
              >
                {instances.items.length === 0 ? (
                  <div className="cc-empty">
                    <div className="cc-empty-text">
                      {wfState === "failed"
                        ? t("home.loadFailed", { defaultValue: "Could not be loaded. Try Refresh." })
                        : t("home.workflows.empty", { defaultValue: "You have not started a workflow case yet." })}
                    </div>
                  </div>
                ) : (
                  <ul className="cc-rows">
                    {instances.items.slice(0, 5).map((inst) => (
                      <li key={inst.id}>
                        <div className="cc-row">
                          <span className="cc-row-main">
                            <span className="cc-row-title">{inst.subjectTitle || inst.archiveNo || `#${inst.id}`}</span>
                            <span className="cc-row-sub">{inst.definitionName}</span>
                          </span>
                          <span className="cc-row-side">
                            <span className={`cc-chip ${inst.isRunning ? "cc-chip--blue" : "cc-chip--green"}`}>
                              {inst.isRunning
                                ? t("home.chip.running", { defaultValue: "Running" })
                                : (inst.outcome || t("home.chip.finished", { defaultValue: "Finished" }))}
                            </span>
                            <span className="cc-row-when">{fmtWhen(inst.completedAt || inst.startedAt, t)}</span>
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </Panel>
            )}

            {govEnabled && (
              <Panel
                marks={2}
                title={t("home.folders.title", { defaultValue: "Your folders" })}
                count={rootFolders.length || null}
                action={<Link className="cc-panel-link" to="/" aria-label={t("home.viewAll", { defaultValue: "View all" })}>{I.chevronRight}</Link>}
              >
                {rootFolders.length === 0 ? (
                  <div className="cc-empty">
                    <div className="cc-empty-text">{t("home.folders.empty", { defaultValue: "No folders have been set up yet." })}</div>
                  </div>
                ) : (
                  <div className="cc-minifolders">
                    {rootFolders.slice(0, 4).map((f) => (
                      <Link key={f.id} className="cc-minifolder cc-cut cc-cut--br" style={{ "--cut": "10px" }} to="/" title={f.path || f.name}>
                        {I.folder}
                        <span className="cc-minifolder-main">
                          <span className="cc-minifolder-name">{f.name}</span>
                          <span className="cc-minifolder-sub">
                            {t("homeCc.subfolders", { defaultValue: "{{count}} subfolders", count: childCount(f.id) })}
                          </span>
                        </span>
                      </Link>
                    ))}
                  </div>
                )}
              </Panel>
            )}

            <Panel
              marks={2}
              title={t("detail.recentlyViewed", { defaultValue: "Recently viewed" })}
              action={<Link className="cc-panel-link" to="/">{t("home.viewAll", { defaultValue: "View all" })}</Link>}
            >
              {recent.length === 0 ? (
                <div className="cc-empty">
                  <div className="cc-empty-text">{t("homeCc.noRecent", { defaultValue: "Nothing has been opened on this browser yet." })}</div>
                </div>
              ) : (
                <ul className="cc-rows">
                  {recent.slice(0, 4).map((r, i) => (
                    <li key={r.id || r.ts || i}>
                      <Link className="cc-row" to="/">
                        <span className="cc-row-doc">{I.doc}</span>
                        <span className="cc-row-main">
                          <span className="cc-row-title">{r.title || t("home.untitled", { defaultValue: "Untitled" })}</span>
                          <span className="cc-row-date">{r.date ? String(r.date).slice(0, 10) : ""}</span>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>
          </div>
        </div>
      </div>

      {/* Where should it go? Asked only when there is a choice to make: one folder goes straight in. */}
      {pickerOpen && (
        <div className="cc-modal-back" onMouseDown={(e) => { if (e.target === e.currentTarget) setPickerOpen(false); }}>
          <div className="cc-modal cc-cut cc-cut--br" role="dialog" aria-modal="true" aria-label={t("homeCc.chooseFolder", { defaultValue: "Which folder?" })}>
            <h2 className="cc-modal-title">{t("homeCc.chooseFolder", { defaultValue: "Which folder?" })}</h2>
            <p className="cc-modal-sub">{t("homeCc.chooseFolderHint", { defaultValue: "The file is archived as a document and filed into the folder you pick." })}</p>
            <div className="cc-modal-list">
              {shortcuts.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  className="cc-modal-choice cc-cut cc-cut--br"
                  style={{ "--cut": "10px" }}
                  onClick={() => { setPickerOpen(false); setUploadInto(f); }}
                >
                  {I.folder}
                  <span className="cc-modal-choice-name">{f.name}</span>
                  {I.chevronRight}
                </button>
              ))}
            </div>
            <div className="cc-modal-foot">
              <button type="button" className="cc-btn" onClick={() => setPickerOpen(false)}>
                {t("common.cancel", { defaultValue: "Cancel" })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* The workspace's own upload dialog, not a second one: it already knows how to put a file into
          the archive AND into the org layers, and how to say why when it cannot. */}
      <WorkspaceUploadModal
        open={!!uploadInto}
        folder={uploadInto}
        onClose={() => setUploadInto(null)}
        onUploaded={onUploaded}
      />

      <PromptModal
        open={newFolderOpen}
        title={t("workspace.newFolder", { defaultValue: "New folder" })}
        label={t("workspace.folderName", { defaultValue: "Folder name" })}
        icon="📁"
        confirmLabel={t("common.create", { defaultValue: "Create" })}
        maxLength={200}
        busy={creating}
        onSubmit={createFolder}
        onCancel={() => setNewFolderOpen(false)}
      />

      {notice && (
        <div className={`cc-toast${notice.kind === "bad" ? " cc-toast--bad" : ""}`} role="status">
          <span>{notice.text}</span>
          <button type="button" onClick={() => setNotice(null)} aria-label={t("common.close", { defaultValue: "Close" })}>×</button>
        </div>
      )}

      <div className="cc-draft-flag">
        {t("homeCc.draftNotice", { defaultValue: "Draft layout — the live home is unchanged" })}
        <Link to="/">{t("homeCc.backToLive", { defaultValue: "Go there" })}</Link>
      </div>
    </div>
  );
}
